import { getGeoLocation } from '@/helpers';
import { showGeolocationErrorToast } from '@/helpers/toast';
import { createContext, Dispatch, useContext, useEffect, useState } from 'react';

type GeolocationPosition = {
  latitude: number;
  longitude: number;
};

async function getUserLocationPermission(
  setPermission: Dispatch<PermissionState | null>,
  setLoading: Dispatch<boolean>,
) {
  const permission = await navigator.permissions.query({ name: 'geolocation' });
  setPermission(permission.state);
  setLoading(false);
}

function requestUserLocationPermission(
  setPosition: Dispatch<GeolocationPosition | null>,
  setPermission: Dispatch<PermissionState | null>,
  setLoading: Dispatch<boolean>,
  callback: (position: GeolocationPosition) => void,
) {
  setLoading(true);

  getGeoLocation(
    (lat: string, lon: string) => {
      const latitude = Number(lat);
      const longitude = Number(lon);
      setPosition({ latitude, longitude });
      callback({ latitude, longitude });
      getUserLocationPermission(setPermission, setLoading);
      setLoading(false);
    },
    null,
    null,
    (error) => {
      setLoading(false);
      showGeolocationErrorToast(error);
    },
    (lat: string, lon: string) => {
      const latitude = Number(lat);
      const longitude = Number(lon);
      callback({ latitude, longitude });
      setPosition({ latitude, longitude });
      setPermission('denied');
      setLoading(false);
    },
  );
}

export type UserLocationPermissionManager = ReturnType<typeof useUserLocationPermissioManager>;

const useUserLocationPermissioManager = () => {
  const [permission, setPermission] = useState<PermissionState | null>(null);
  const [position, setPosition] = useState<GeolocationPosition | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if ('geolocation' in navigator && navigator.permissions && navigator.permissions.query) {
      getUserLocationPermission(setPermission, setLoading);
    }
  }, []);

  const handleRequestUserLocationPermission = (callback: (position: GeolocationPosition) => void) => {
    requestUserLocationPermission(setPosition, setPermission, setLoading, callback);
  };

  return {
    position,
    permission,
    loading,
    onRequestUserLocationPermission: handleRequestUserLocationPermission,
  };
};

const UseLocationPermissionContext = createContext<UserLocationPermissionManager>({
  loading: false,
  permission: null,
  position: null,
  onRequestUserLocationPermission: () => {},
});

export const useLocationPermissionContext = () => {
  const context = useContext(UseLocationPermissionContext);
  if (!context) {
    throw new Error('useLocationPermissionContext must be used within a LocationPermissionProvider');
  }
  return context;
};

export const LocationPermissionProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <UseLocationPermissionContext.Provider value={useUserLocationPermissioManager()}>
      {children}
    </UseLocationPermissionContext.Provider>
  );
};
